// Global navigation provider. Owns the top-level destinations and turns each
// into a command that navigates.
//
// Add a destination by adding an entry to NAV_ITEMS. `getNavigationShortcuts`
// exposes the visible ones to the keyboard-chord handler.

// Each item: { id, label, path?, icon?, group?, keywords?, shortcut?, visible?, onSelect? }
// visible:  ({ user, isFeatureEnabled }) => boolean
// onSelect: (api) => void — custom behavior; defaults to navigating to `path`.

import { useDiscoveryHubStore } from "app/discovery-hub/store"

// Help actions mirror the "Get help" submenu in the account menu
// (app/navigation/user-account-menu.jsx).
const openExternal = url => window.open(url, "_blank", "noopener,noreferrer")

const openAIAssistant = () => {
  // Intercom is injected globally when available; otherwise fall back to docs.
  if (typeof window.Intercom === "function") {
    window.Intercom("showNewMessage")
  } else {
    openExternal("https://help.kit.com/")
  }
}

// The whole Settings group is hidden unless the user can view account settings —
// mirrors the server's `can?(:view_account_settings)`, surfaced to the frontend
// as the `account_settings.read` activity (user.canViewAccountSettings). The
// profile tabs (Login details, Notifications) are always shown, so they aren't
// gated. We intentionally don't reproduce the server's shopify-reviewer and
// commerce-terms gates (no frontend signal); those pages stay server-authorized.
const canViewSettings = ({ user }) => Boolean(user.canViewAccountSettings)

const NAV_ITEMS = [
  {
    id: "dashboard",
    label: "Dashboard",
    path: "/dashboard",
    shortcut: "g d"
  },
  {
    id: "subscribers",
    label: "Subscribers",
    path: "/subscribers",
    shortcut: "g s"
  },
  {
    id: "broadcasts",
    label: "Broadcasts",
    path: "/campaigns",
    keywords: ["campaigns", "email"],
    shortcut: "g b"
  },
  {
    id: "landing-pages-forms",
    label: "Landing pages & forms",
    path: "/forms",
    keywords: ["forms", "landing"],
    shortcut: "g f"
  },
  {
    id: "recommendations",
    label: "Recommendations",
    path: "/creator-network",
    keywords: ["creator network"]
  },
  {
    id: "newsletter-site",
    label: "Newsletter site",
    path: "/newsletter-site",
    keywords: ["creator profile"]
  },
  {
    id: "products",
    label: "Products",
    path: "/products",
    shortcut: "g p"
  },
  {
    id: "sponsorships",
    label: "Sponsorships",
    path: "/ads",
    keywords: ["ads", "advertising"]
  },
  {
    id: "paid-recommendations",
    label: "Paid recommendations",
    path: "/paid_recommendations",
    visible: ({ user }) => user.canUsePaidRecommendations
  },
  { id: "tips", label: "Tips", path: "/tips" },
  {
    id: "payouts",
    label: "Payouts",
    path: "/account_settings/commerce"
  },
  {
    id: "automations",
    label: "Visual automations",
    path: "/automations",
    keywords: ["funnels"],
    shortcut: "g a"
  },
  { id: "rules", label: "Rules", path: "/rules" },
  {
    id: "sequences",
    label: "Email sequences",
    path: "/sequences",
    keywords: ["courses"],
    shortcut: "g e"
  },
  {
    id: "webhooks",
    label: "Webhooks",
    path: "/webhooks",
    visible: ({ isFeatureEnabled }) => isFeatureEnabled("webhooks_v2_ui")
  },
  {
    id: "kit-mcp",
    label: "Kit MCP",
    path: "/kit-mcp",
    keywords: ["ai"],
    visible: ({ isFeatureEnabled }) => !isFeatureEnabled("ai_connectors_rename")
  },
  {
    id: "ai-connectors",
    label: "AI connectors",
    path: "/ai-connectors",
    keywords: ["ai", "mcp"],
    visible: ({ isFeatureEnabled }) => isFeatureEnabled("ai_connectors_rename")
  },
  { id: "rss", label: "RSS", path: "/feeds", keywords: ["feeds"] },
  {
    id: "apps",
    label: "Apps",
    path: "/apps",
    keywords: ["integrations"]
  },
  {
    id: "snippets",
    label: "Snippets",
    path: "/account/snippets"
  },
  {
    id: "email_templates",
    label: "Email templates",
    path: "/account/email_layout_templates"
  },
  {
    id: "settings",
    label: "Settings",
    path: "/account_settings"
  },
  {
    id: "settings-login-details",
    label: "Login details",
    path: "/account_settings/account_info",
    group: "Settings",
    keywords: ["settings", "password", "two-factor", "2fa", "login email"]
  },
  {
    id: "settings-notifications",
    label: "Notifications",
    path: "/account_settings/account_notifications",
    group: "Settings",
    keywords: ["settings", "emails"]
  },
  {
    id: "settings-general",
    label: "General",
    path: "/account_settings/general",
    group: "Settings",
    keywords: [
      "settings",
      "brand",
      "business name",
      "colors",
      "links",
      "favicon"
    ],
    visible: canViewSettings
  },
  {
    id: "settings-email",
    label: "Email",
    path: "/account_settings/email",
    group: "Settings",
    keywords: ["settings", "from address", "sending domain", "timezone"],
    visible: canViewSettings
  },
  {
    id: "settings-domains",
    label: "Domains",
    path: "/account_settings/custom_domains",
    group: "Settings",
    keywords: ["settings", "custom domain"],
    visible: canViewSettings
  },
  {
    id: "settings-revenue",
    label: "Revenue",
    path: "/account_settings/commerce",
    group: "Settings",
    keywords: ["settings", "stripe", "tax", "payouts", "currency", "commerce"],
    visible: canViewSettings
  },
  {
    id: "settings-billing",
    label: "Account & Billing",
    path: "/account_settings/billing",
    group: "Settings",
    keywords: ["settings", "plan", "payment", "invoice"],
    visible: canViewSettings
  },
  {
    id: "settings-team",
    label: "Team",
    path: "/account_settings/team",
    group: "Settings",
    keywords: ["settings", "members", "roles", "access"],
    visible: ({ user }) => canViewSettings({ user }) && !user.access_level?.free
  },
  {
    id: "settings-advanced",
    label: "Advanced",
    path: "/account_settings/advanced_settings",
    group: "Settings",
    keywords: ["settings", "affiliate", "tracking", "bot"],
    visible: canViewSettings
  },
  {
    id: "settings-developer",
    label: "Developer",
    path: "/account_settings/developer_settings",
    group: "Settings",
    keywords: ["settings", "api keys", "apps"],
    visible: canViewSettings
  },
  {
    id: "settings-kit-mcp",
    label: "Kit MCP",
    path: "/account_settings/kit_mcp",
    group: "Settings",
    keywords: ["settings", "mcp", "ai"],
    visible: canViewSettings
  },
  {
    id: "whats-new",
    label: "What's new",
    group: "Help",
    keywords: ["changelog", "updates", "release notes", "discovery"],
    onSelect: api => {
      api.close()
      useDiscoveryHubStore.getState().setOpen(true)
    }
  },
  {
    id: "help-ai-assistant",
    label: "AI assistant",
    group: "Help",
    keywords: ["chat", "intercom", "ask", "contact", "support", "help"],
    onSelect: api => {
      api.close()
      openAIAssistant()
    }
  },
  {
    id: "help-documentation",
    label: "Documentation",
    group: "Help",
    keywords: ["docs", "help center", "guides"],
    onSelect: api => {
      api.close()
      openExternal("https://help.kit.com/")
    }
  },
  {
    id: "help-community",
    label: "Community",
    group: "Help",
    keywords: ["forum", "creators"],
    onSelect: api => {
      api.close()
      openExternal("/community")
    }
  },
  {
    id: "help-creator-university",
    label: "Creator University",
    group: "Help",
    keywords: ["courses", "learn", "training", "university"],
    onSelect: api => {
      api.close()
      openExternal("https://learn.kit.com/courses")
    }
  }
]

const isVisible = (item, context) =>
  item.visible
    ? item.visible({
        user: context.user,
        isFeatureEnabled: context.isFeatureEnabled
      })
    : true

/**
 * Visible navigation destinations that have a keyboard chord, as
 * {@link import("../types").Shortcut}s that navigate. Consumed by the hotkeys
 * handler so chords respect the same gating as the rendered list.
 *
 * @param {import("../types").CommandContext} context
 * @returns {import("../types").Shortcut[]}
 */
export function getNavigationShortcuts(context) {
  return NAV_ITEMS.filter(
    item => item.shortcut && isVisible(item, context)
  ).map(item => ({
    shortcut: item.shortcut,
    onTrigger: api => api.navigate(item.path)
  }))
}

/** @type {import("../types").Provider} */
export function useNavigationProvider({ activePageId, context }) {
  // Root-only: a subpage is a focused mode, so navigation steps aside for it.
  if (activePageId) return { commands: [], isLoading: false }

  const commands = NAV_ITEMS.filter(item => isVisible(item, context)).map(
    item => ({
      id: item.id,
      label: item.label,
      icon: "arrow-right",
      group: item.group || "Navigation",
      keywords: item.keywords,
      shortcut: item.shortcut,
      // Items that just navigate to `path` expose it as `href` so the row is a
      // real link (middle-click / ⌘-click → new tab). Items with a custom
      // onSelect (e.g. Help modals) aren't links.
      href: item.onSelect ? undefined : item.path,
      // Most items just navigate to `path`; some (e.g. Help) supply their own.
      onSelect: item.onSelect ?? (api => api.navigate(item.path))
    })
  )

  return { commands, isLoading: false }
}
